import { useEffect, useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MapPin, Calendar, Eye, EyeOff } from "lucide-react";
import AdminToggle from "@/components/admin/AdminToggle";

/**
 * Admin-only card that toggles the two landing marketing modules and
 * optionally schedules a promo window per module (auto-enable at X,
 * auto-disable at Y). Also renders a tiny live preview of each
 * module so admins can see what they are turning on or off.
 *
 * Reads and writes the same `/landing/flags` API as the public
 * landing components — so any change is instantly reflected the next
 * time the page loads. Every save is written to the audit log
 * server-side.
 */

const LABELS = {
  state_banner: "State-request banner",
  compare_strip: "Compare-plans strip",
};

const ROWS = [
  { key: "state_banner", hint: "Top banner: Now serving Florida + email waitlist form" },
  { key: "compare_strip", hint: "Slim price strip under the banner" },
];

// datetime-local <input> uses the browser's local timezone in the form
// `YYYY-MM-DDTHH:MM`; convert to/from the ISO UTC string the backend
// stores so admins see local times and the schedule works globally.
function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function localInputToIso(local) {
  if (!local) return null;
  try {
    const d = new Date(local);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch { return null; }
}

export default function LandingFlagsCard() {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/landing/flags");
        if (!cancelled) setFlags(data || {});
      } catch { if (!cancelled) setFlags({}); }
    })();
    return () => { cancelled = true; };
  }, []);

  async function save(key, patch) {
    setSaving(key);
    try {
      const { data } = await API.put("/admin/landing/flags", patch);
      setFlags(data);
      toast.success(`${LABELS[key]} saved`);
    } catch {
      toast.error("Couldn't save — try again");
    } finally {
      setSaving(null);
    }
  }

  function toggleBase(key) {
    // `flags[key]` is the *effective* boolean including the schedule.
    // Prefer the base value when toggling manually so admins are
    // flipping the intended state, not the currently-observed one.
    const baseKey = `${key}_base`;
    const nextBase = !(flags[baseKey] ?? flags[key]);
    setFlags((f) => ({ ...f, [key]: nextBase, [baseKey]: nextBase })); // optimistic
    save(key, { [key]: nextBase });
  }

  function saveSchedule(key, enable_at, disable_at) {
    save(key, {
      [`${key}_schedule`]: {
        enable_at: enable_at || null,
        disable_at: disable_at || null,
      },
    });
  }

  if (!flags) return null;

  return (
    <div
      className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5"
      data-testid="admin-landing-flags-card"
    >
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Landing page modules
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">Flip on/off live · schedule promo windows · audit-logged</p>
      </div>
      <div className="space-y-4">
        {ROWS.map((r) => (
          <FlagRow
            key={r.key}
            flagKey={r.key}
            label={LABELS[r.key]}
            hint={r.hint}
            effective={!!flags[r.key]}
            base={flags[`${r.key}_base`] ?? flags[r.key]}
            schedule={flags[`${r.key}_schedule`] || {}}
            busy={saving === r.key}
            onToggle={() => toggleBase(r.key)}
            onSaveSchedule={(ea, da) => saveSchedule(r.key, ea, da)}
          />
        ))}
      </div>
    </div>
  );
}

function FlagRow({ flagKey, label, hint, effective, base, schedule, busy, onToggle, onSaveSchedule }) {
  const [enableAt, setEnableAt] = useState(isoToLocalInput(schedule?.enable_at));
  const [disableAt, setDisableAt] = useState(isoToLocalInput(schedule?.disable_at));
  const [showSched, setShowSched] = useState(!!(schedule?.enable_at || schedule?.disable_at));

  useEffect(() => {
    setEnableAt(isoToLocalInput(schedule?.enable_at));
    setDisableAt(isoToLocalInput(schedule?.disable_at));
  }, [schedule?.enable_at, schedule?.disable_at]);

  return (
    <div
      className="rounded-lg border border-slate-200 dark:border-slate-800 p-4"
      data-testid={`landing-flag-${flagKey}`}
    >
      <div className="flex items-start gap-3">
        <AdminToggle
          checked={!!base}
          busy={busy}
          onCheckedChange={onToggle}
          ariaLabel={`Toggle ${label}`}
          testId={`landing-flag-${flagKey}-toggle`}
          showBadge={false}
          className="mt-0.5 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{label}</p>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                effective
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
              data-testid={`landing-flag-${flagKey}-status-badge`}
            >
              {effective ? <><Eye className="w-3 h-3" /> Live</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-3">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-1">Preview</p>
        {flagKey === "state_banner" ? <StateBannerPreview /> : <CompareStripPreview />}
      </div>

      {/* Schedule */}
      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setShowSched((s) => !s)}
          className="text-xs font-semibold text-purple-600 dark:text-purple-400 inline-flex items-center gap-1.5 hover:underline"
          data-testid={`landing-flag-${flagKey}-schedule-toggle`}
        >
          <Calendar className="w-3.5 h-3.5" />
          {showSched ? "Hide schedule" : "Add schedule (auto on/off)"}
        </button>
        {showSched ? (
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            <label className="text-xs text-slate-600 dark:text-slate-300">
              <span className="block mb-1 font-semibold">Auto-enable at</span>
              <input
                type="datetime-local"
                value={enableAt}
                onChange={(e) => setEnableAt(e.target.value)}
                className="w-full text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 py-1.5"
                data-testid={`landing-flag-${flagKey}-enable-at`}
              />
            </label>
            <label className="text-xs text-slate-600 dark:text-slate-300">
              <span className="block mb-1 font-semibold">Auto-disable at</span>
              <input
                type="datetime-local"
                value={disableAt}
                onChange={(e) => setDisableAt(e.target.value)}
                className="w-full text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 py-1.5"
                data-testid={`landing-flag-${flagKey}-disable-at`}
              />
            </label>
            <div className="sm:col-span-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => onSaveSchedule(localInputToIso(enableAt), localInputToIso(disableAt))}
                disabled={busy}
                className="text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded px-3 py-1.5 disabled:opacity-60"
                data-testid={`landing-flag-${flagKey}-schedule-save`}
              >
                Save schedule
              </button>
              {(enableAt || disableAt) ? (
                <button
                  type="button"
                  onClick={() => { setEnableAt(""); setDisableAt(""); onSaveSchedule(null, null); }}
                  disabled={busy}
                  className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
                  data-testid={`landing-flag-${flagKey}-schedule-clear`}
                >
                  Clear
                </button>
              ) : null}
              <p className="text-[11px] text-slate-500 dark:text-slate-400 ml-auto">
                Times are in your local timezone. During the window the module shows regardless of the toggle above; outside it, the module hides.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StateBannerPreview() {
  return (
    <div className="rounded overflow-hidden">
      <div
        className="text-white text-[10px] px-3 py-1.5 flex items-center gap-2"
        style={{ background: "linear-gradient(90deg, #7e22ce 0%, #a855f7 50%, #c026d3 100%)" }}
      >
        <MapPin className="w-3 h-3 shrink-0" />
        <span className="truncate">
          🌴 Now serving Florida · Starting dose from <span className="font-bold">$79.99</span> · East Coast next
        </span>
        <span className="ml-auto bg-white text-purple-700 rounded px-2 py-0.5 text-[9px] font-bold">Request</span>
      </div>
    </div>
  );
}

function CompareStripPreview() {
  return (
    <div className="rounded overflow-hidden">
      <div className="bg-slate-950 text-white text-[10px] px-3 py-1.5 flex items-center gap-2">
        <span className="text-fuchsia-300 font-bold uppercase tracking-wider">Compare</span>
        <span className="bg-white/10 border border-white/10 rounded px-1.5 py-0.5">Starter · <b>$79.99</b></span>
        <span className="bg-white/10 border border-white/10 rounded px-1.5 py-0.5">Semaglutide · <b>$149/mo</b></span>
        <span className="bg-white/10 border border-white/10 rounded px-1.5 py-0.5 hidden md:inline">Tirzepatide · <b>$249/mo</b></span>
      </div>
    </div>
  );
}
